import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'antd';
import { useStore } from '../../store/store';
import { getContainerById } from '../../store/lookups';
import { getSectionRecursiveMeta } from '../../utils/recursiveSections';
import { recursiveModelColor } from '../../utils/recursiveModelColor';
import RecursiveModelDot from '../common/RecursiveModelDot';
import { scrollToItem } from '../Middle/InfinitePage';
import BoxFormControlsPortal from './BoxFormControlsPortal';
import { PRIMARY_DARK } from '../../constants/constants';

// Contexte de la section, en mode PROPOSITION uniquement.
//
// Une section récursive injectée est « bakée » : ses containers sont dépliés en
// N occurrences (une par ligne du modèle — un poste de travail, une tenue…),
// mêlées au reste du document. Sur le canvas, rien ne distingue la 2e occurrence
// de la 4e, ni le contenu d'une sous-section de celui de son parent. L'arbre du
// panel gauche montre l'imbrication, mais pas *où* on se trouve dedans.
//
// Ce bandeau répond aux deux questions depuis la sélection courante :
//  - « où suis-je » : le fil d'Ariane, lu depuis `sectionPath` (chaîne d'ids de
//    section racine -> section courante, persistée à l'injection, cf.
//    utils/sections.js). Chaque segment est cliquable et fait défiler le canvas
//    jusqu'à l'ancêtre correspondant.
//  - « pourquoi je vois ça N fois » : le compteur d'occurrence, dérivé des
//    `dateTimestamp` distincts partageant le même chemin — le même découpage que
//    l'arbre gauche (utils/sectionTree.js), pour que les deux vues concordent.
//
// La puce de couleur du modèle récursif est celle de l'arbre gauche
// (utils/recursiveModelColor.js) : c'est le repère qui relie les deux panels.

// Dernière position du panel, juste avant l'origine du contenu (99, mode
// modèle) : information de contexte, pas un réglage. En tête, elle décalait
// Actions/Affichage/Texte selon que la sélection vienne d'une section ou non,
// et les boutons changeaient de place d'une sélection à l'autre.
const SECTION_CONTEXT_ORDER = 98;

const pathKeyOf = (path) => (Array.isArray(path) ? path.map(String).join('>') : null);

const SectionContextPanel = () => {
  const { t } = useTranslation();
  // Slices brutes plutôt qu'un objet dérivé : le calcul ci-dessous produit des
  // tableaux, qu'un `shallow` ne saurait pas comparer (nouvelle référence à
  // chaque appel du sélecteur = re-render à chaque frappe du document).
  const isTemplate = useStore((state) => state.currentDocument?.type === 'template');
  const selectedBoxId = useStore((state) => state.selectedBoxId);
  const activeContainerId = useStore((state) => state.activeContainerId);
  const boxes = useStore((state) => state.boxes);
  const containers = useStore((state) => state.containers);
  const sections = useStore((state) => state.sections);
  const recursiveModelLabels = useStore((state) => state.recursiveModelLabels);

  const context = useMemo(() => {
    if (isTemplate) return null;

    // La box sélectionnée porte le chemin le plus précis (une sous-section a son
    // propre `sectionPath`) ; sans box sélectionnée, on lit le container actif.
    const selectedBox = selectedBoxId ? boxes.find((b) => b.id === selectedBoxId) : null;
    const container = getContainerById(containers, activeContainerId);
    const source = selectedBox ?? container;
    const path = Array.isArray(source?.sectionPath) ? source.sectionPath.map(String) : null;
    if (!path || path.length === 0) return null;

    const sectionsById = new Map(sections.map((s) => [String(s.id), s]));
    const labelOf = (sectionId) => {
      const section = sectionsById.get(String(sectionId));
      if (section?.name) return section.name;
      const meta = getSectionRecursiveMeta(section);
      if (meta.recursiveModelName) {
        return recursiveModelLabels?.[meta.recursiveModelName] ?? meta.recursiveModelName;
      }
      return t('section_context_unknown');
    };

    const key = pathKeyOf(path);

    // Occurrences : containers partageant exactement ce chemin, regroupés par
    // `dateTimestamp` (une expansion = un timestamp). Ordre d'apparition dans le
    // document et non ordre numérique : « occurrence 2 » doit désigner la
    // deuxième qu'on rencontre en descendant la page.
    const timestamps = [];
    let deepest = path.length;
    containers.forEach((c) => {
      const cPath = Array.isArray(c?.sectionPath) ? c.sectionPath.map(String) : null;
      if (!cPath || cPath.length === 0) return;
      // Profondeur maximale atteinte dans la même branche (même racine) : donne
      // le dénominateur du niveau, « niv. 2/3 ».
      if (cPath[0] === path[0] && cPath.length > deepest) deepest = cPath.length;
      if (pathKeyOf(cPath) !== key) return;
      if (c.dateTimestamp == null) return;
      if (!timestamps.includes(c.dateTimestamp)) timestamps.push(c.dateTimestamp);
    });

    const occurrenceIndex =
      source.dateTimestamp != null ? timestamps.indexOf(source.dateTimestamp) : -1;

    const tipSection = sectionsById.get(path[path.length - 1]);
    const tipMeta = getSectionRecursiveMeta(tipSection);

    return {
      segments: path.map((sectionId, index) => ({
        sectionId,
        label: labelOf(sectionId),
        // Chemin partiel : sert à retrouver le container de cet ancêtre.
        prefixKey: pathKeyOf(path.slice(0, index + 1)),
        isTip: index === path.length - 1,
      })),
      depth: path.length,
      deepest,
      modelName: tipMeta.recursiveModelName,
      modelLabel: tipMeta.recursiveModelName
        ? recursiveModelLabels?.[tipMeta.recursiveModelName] ?? tipMeta.recursiveModelName
        : null,
      // Compteur réservé aux sections récursives, comme le ×N de l'arbre gauche
      // (utils/sectionTree.js). Une même section liée insérée deux fois dans le
      // document produirait sinon « occurrence 1 / 2 », alors qu'il s'agit de deux
      // insertions distinctes et non de la répétition d'un modèle.
      occurrenceTotal: tipMeta.isRecursive && timestamps.length > 1 ? timestamps.length : 0,
      occurrenceNumber: occurrenceIndex >= 0 ? occurrenceIndex + 1 : null,
    };
  }, [isTemplate, selectedBoxId, activeContainerId, boxes, containers, sections, recursiveModelLabels, t]);

  if (!context) return null;

  const { segments, depth, deepest, modelName, modelLabel, occurrenceTotal, occurrenceNumber } =
    context;

  // Défile jusqu'au premier contenu de l'ancêtre cliqué. Même résolution que
  // l'arbre gauche : container au chemin exact -> sa première box.
  const goToSegment = (prefixKey) => {
    const target = containers.find((c) => pathKeyOf(c?.sectionPath) === prefixKey);
    if (!target || !Array.isArray(target.columnsIds)) return;
    const box = boxes.find((b) => target.columnsIds.includes(b.columnId));
    if (box) scrollToItem(box);
  };

  const accent = recursiveModelColor(modelName) ?? PRIMARY_DARK;

  return (
    <BoxFormControlsPortal
      title={t('section_context_title')}
      layout='column'
      order={SECTION_CONTEXT_ORDER}
      // Contexte de section du container : indépendant de la box sélectionnée.
      keepOnMultiSelection
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 12 }}>
        <RecursiveModelDot modelName={modelName} label={modelLabel} />
        {segments.map((segment, index) => (
          <React.Fragment key={segment.prefixKey}>
            {index > 0 && <span style={{ color: 'rgba(11, 34, 57, 0.35)' }}>›</span>}
            {/* Libellé tronqué (ellipsis) : le survol le donne en entier. Tooltip
                antd direct et non common/Tooltip, coupé dans tout le panneau
                (cf. BoxFormControlsPortal) ; le `title` natif n'y apparaissait pas. */}
            <Tooltip title={segment.label} placement='top' mouseEnterDelay={0.3}>
              <span
                role='button'
                tabIndex={-1}
                onClick={() => goToSegment(segment.prefixKey)}
                style={{
                  cursor: 'pointer',
                  color: segment.isTip ? accent : 'rgba(11, 34, 57, 0.6)',
                  fontWeight: segment.isTip ? 700 : 400,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {segment.label}
              </span>
            </Tooltip>
          </React.Fragment>
        ))}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          fontSize: 11,
          color: 'rgba(11, 34, 57, 0.65)',
        }}
      >
        <span
          style={{
            background: 'rgba(11, 34, 57, 0.06)',
            borderRadius: 8,
            padding: '1px 8px',
            fontWeight: 600,
          }}
        >
          {t('section_context_level', { depth, total: Math.max(deepest, depth) })}
        </span>
        {occurrenceTotal > 0 && occurrenceNumber && (
          <span
            style={{
              background: `${accent}1A`,
              color: accent,
              borderRadius: 8,
              padding: '1px 8px',
              fontWeight: 600,
            }}
          >
            {t('section_context_occurrence', { index: occurrenceNumber, total: occurrenceTotal })}
          </span>
        )}
      </div>

      {occurrenceTotal > 0 && modelLabel && (
        <div style={{ fontSize: 11, lineHeight: 1.4, color: 'rgba(11, 34, 57, 0.65)' }}>
          {t('section_context_repeat_hint', { model: modelLabel, total: occurrenceTotal })}
        </div>
      )}
    </BoxFormControlsPortal>
  );
};

export default SectionContextPanel;
